import { safeLocalStorage } from '@/lib/safeStorage';
import {useCallback, useEffect, useRef, useState} from 'react';
import {getIdToken, getRefreshToken} from '../lib/api';
import {refreshTokenIfNeeded} from '../lib/api/tokenRefresh';
import {jwtDecode} from 'jwt-decode';
import {ALL_PERMISSIONS_MASK} from '@ruta/shared';
import {AUTH_EVENTS, STORAGE_KEYS} from '../lib/constants';
import posthog from 'posthog-js';
import {clearPendingSignupIdentity, isPendingSignupIdentity} from '../lib/signupLead';

export interface AuthStatusResult {
  isAuthenticated: boolean;
  inOrg: boolean;
  loading: boolean;
  orgId: string | null;
  permissionMask: number;
}

/**
 * Only a PostHog that identified someone needs a reset on sign-out — and not the
 * signup in progress: step 1 identifies the new login before it has a token
 * (RUT-4448), and a reload before the code must keep that person.
 */
export function shouldResetPostHog(client: { _isIdentified?: () => boolean; get_distinct_id?: () => string }): boolean {
  try {
    if (typeof client.get_distinct_id === 'function' && isPendingSignupIdentity(client.get_distinct_id())) return false;
    return typeof client._isIdentified === 'function' ? client._isIdentified() : true;
  } catch {
    return true;
  }
}

export function useAuthStatus(): AuthStatusResult {
  const [status, setStatus] = useState<AuthStatusResult>({
    isAuthenticated: false,
    inOrg: false,
    loading: true,
    orgId: null,
    permissionMask: 0,
  });

  const isInitializedRef = useRef(false);

  const setUnauthenticated = useCallback(() => {
    // Reset PostHog user identification when not authenticated — but only when
    // someone was identified. This runs on every mount of every useAuthStatus
    // caller, and resetting an anonymous visitor gave each signup page load a
    // new id and session, so its replay never flushed (RUT-4433).
    if (!import.meta.env.DEV && !window.location.hostname.includes('localhost')) {
      try {
        if (shouldResetPostHog(posthog)) posthog.reset();
      } catch (posthogError) {
        console.error('[useAuthStatus] PostHog reset error:', posthogError);
      }
    }

    setStatus({
      isAuthenticated: false,
      inOrg: false,
      loading: false,
      orgId: null,
      permissionMask: 0,
    });
    isInitializedRef.current = true;
  }, []);

  const setAuthenticatedFromToken = useCallback((t: string) => {
    try {
      const decoded = jwtDecode<any>(t);
      const orgId = decoded['custom:orgId'];
      const permissionsStr = decoded['custom:permissions'];
      // Local runs always use the full mask — the token's mask can lag behind
      // PERMISSION_ORDER and silently hide permission-gated screens.
      const permissionMask =
        import.meta.env.DEV || window.location.hostname === 'localhost'
          ? ALL_PERMISSIONS_MASK
          : permissionsStr
            ? parseInt(permissionsStr, 10)
            : 0;

      // Identify user in PostHog when authenticated
      if (!import.meta.env.DEV && !window.location.hostname.includes('localhost')) {
        try {
          const userId = decoded.sub;
          const userEmail = decoded.email;
          const userName = decoded.given_name && decoded.family_name
            ? `${decoded.given_name} ${decoded.family_name}`
            : decoded.name || userEmail?.split('@')[0] || 'Unknown User';
          // The same id usePostHogIdentify and signup step 1 use, so one admin is one
          // person from the first signup page on (RUT-4448).
          posthog.identify(`admin:${userId}`, {
            email: userEmail,
            name: userName,
            organization_id: orgId,
            given_name: decoded.given_name,
            family_name: decoded.family_name,
          });
        } catch (posthogError) {
          console.error('[useAuthStatus] PostHog identification error:', posthogError);
        }
      }
      // Signed in: the signup's pending id has done its job.
      clearPendingSignupIdentity();

      setStatus({
        isAuthenticated: true,
        inOrg: !!orgId,
        loading: false,
        orgId,
        permissionMask,
      });
    } catch (error) {
      console.error('[useAuthStatus] JWT decode error:', error);
      setUnauthenticated();
    }
    isInitializedRef.current = true;
  }, [setUnauthenticated]);

  const checkAuthStatus = useCallback(async () => {
    console.log('[useAuthStatus] Starting auth check...');

    const t = getIdToken();
    console.log('[useAuthStatus] ID token present:', !!t);

    // Check if user has explicitly logged out
    const hasLoggedOut = safeLocalStorage.getItem(STORAGE_KEYS.HAS_LOGGED_OUT) === 'true';

    if (!t) {
      // If ID token is expired but refresh token exists, try refreshing
      const refreshToken = getRefreshToken();
      if (refreshToken && !hasLoggedOut) {
        console.log('[useAuthStatus] ID token expired but refresh token exists, attempting refresh...');
        const newToken = await refreshTokenIfNeeded(true);
        if (newToken) {
          console.log('[useAuthStatus] Token refresh successful');
          setAuthenticatedFromToken(newToken);
          return;
        }
        console.log('[useAuthStatus] Token refresh failed');
      }

      // Local development fallback - provide fake auth status ONLY if user hasn't logged out
      if ((import.meta.env.DEV || window.location.hostname === 'localhost') && !hasLoggedOut) {
        console.log('[useAuthStatus] Using dev environment fallback auth');
        setStatus({
          isAuthenticated: true,
          inOrg: true,
          loading: false,
          orgId: 'dev-org-id',
          permissionMask: ALL_PERMISSIONS_MASK,
        });
        isInitializedRef.current = true;
        return;
      }

      console.log('[useAuthStatus] No valid token, setting unauthenticated state');
      setUnauthenticated();
      return;
    }

    setAuthenticatedFromToken(t);
  }, [setAuthenticatedFromToken, setUnauthenticated]);

  useEffect(() => {
    console.log('[useAuthStatus] Initial auth check');
    checkAuthStatus();

    // Listen for token updates
    const handleTokensUpdated = () => {
      console.log('[useAuthStatus] Tokens updated event received');
      checkAuthStatus();
    };

    window.addEventListener(AUTH_EVENTS.TOKENS_UPDATED, handleTokensUpdated);

    return () => {
      window.removeEventListener(AUTH_EVENTS.TOKENS_UPDATED, handleTokensUpdated);
    };
  }, [checkAuthStatus]);

  return status;
}
